import * as Yup from 'yup'
import clsx from 'clsx'
import {useFormik} from 'formik'
import {useMutation} from '@apollo/client'
import {Link, useLocation, useNavigate} from 'react-router-dom'
import {useState} from 'react'
import {toast} from 'react-toastify'
import {toAbsoluteUrl} from '../../../../_metronic/helpers'
import {LanguageToggle} from '../../../../_metronic/partials/layout/header-menus/LanguageToggle'
import {VERSION_APP} from '../../helpers/consts'
import {
  PASSWORD_MIN_LENGTH,
  PASSWORD_REQUIREMENTS,
  PASSWORD_VALIDATION_MESSAGES,
} from '../../helpers/passwordPolicy'
import {defineMessages, useIntl} from 'react-intl'
import {FORCE_CHANGE_PASSWORD, RESET_PASSWORD} from '../../gql/mutations/authMutations'

const M = defineMessages({
  titleForced: {id: 'auth.reset.title.forced', defaultMessage: 'Change password'},
  titleInvite: {id: 'auth.reset.title.invite', defaultMessage: 'Set up your account'},
  titleReset: {id: 'auth.reset.title.reset', defaultMessage: 'Reset password'},
  subtitleForced: {
    id: 'auth.reset.subtitle.forced',
    defaultMessage: 'Enter your current password and set a new one to continue.',
  },
  subtitleInvite: {
    id: 'auth.reset.subtitle.invite',
    defaultMessage: 'Create a password to activate your account.',
  },
  subtitleReset: {
    id: 'auth.reset.subtitle.reset',
    defaultMessage: 'Choose a new password for your account.',
  },

  missingEmail: {
    id: 'auth.reset.missingEmail',
    defaultMessage: 'Missing email for forced password change.',
  },
  invalidLink: {
    id: 'auth.reset.invalidLink',
    defaultMessage: 'Invalid or expired reset link.',
  },
  updated: {id: 'auth.reset.updated', defaultMessage: 'Password updated.'},
  changeFailed: {id: 'auth.reset.changeFailed', defaultMessage: 'Password change failed.'},
  doneRedirect: {
    id: 'auth.reset.doneRedirect',
    defaultMessage: 'Password updated. Redirecting to login.',
  },

  requirementsTitle: {
    id: 'auth.reset.requirementsTitle',
    defaultMessage: 'Password requirements:',
  },
  requirementDifferent: {
    id: 'auth.reset.requirementDifferent',
    defaultMessage: 'Must be different from your current password',
  },

  fieldCurrentPassword: {
    id: 'auth.reset.field.currentPassword',
    defaultMessage: 'Current password',
  },
  fieldNewPassword: {id: 'auth.reset.field.newPassword', defaultMessage: 'New password'},
  fieldConfirmPassword: {
    id: 'auth.reset.field.confirmPassword',
    defaultMessage: 'Confirm password',
  },

  submitForced: {id: 'auth.reset.title.forced', defaultMessage: 'Change password'},
  submitInvite: {id: 'auth.reset.submit.invite', defaultMessage: 'Set password'},
  submitReset: {id: 'auth.reset.submit.reset', defaultMessage: 'Update password'},

  // Compartidas por las tres pantallas de auth
  tagline: {id: 'auth.tagline', defaultMessage: 'Fast, Efficient and Productive'},
  backToLogin: {id: 'auth.backToLogin', defaultMessage: 'Back to login'},
  logoAlt: {id: 'auth.logoAlt', defaultMessage: 'Logo'},
  genericError: {
    id: 'auth.error.generic',
    defaultMessage: 'An error occurred. Please try again.',
  },

  // Reusadas
  email: {id: 'common.field.email', defaultMessage: 'Email'},
  pleaseWait: {id: 'admin.oneUser.pleaseWait', defaultMessage: 'Please wait...'},

  // Validacion propia de esta pantalla
  currentPasswordRequired: {
    id: 'validation.currentPassword.required',
    defaultMessage: 'Current password is required',
  },
  passwordDifferent: {
    id: 'validation.password.differentFromCurrent',
    defaultMessage: 'New password must be different from your current password',
  },
  confirmRequired: {
    id: 'validation.confirmPassword.required',
    defaultMessage: 'Confirm password is required',
  },
  minSymbols: {id: 'validation.minSymbols', defaultMessage: 'Minimum {min} symbols'},
  maxSymbols: {id: 'validation.maxSymbols', defaultMessage: 'Maximum {max} symbols'},
})

const PASSWORD_MIN_SYMBOLS = 3
const PASSWORD_MAX_SYMBOLS = 50

export const ResetPasswordPage = () => {
  const intl = useIntl()
  const defaultLogo = toAbsoluteUrl('/media/tfi/logo_policytools.png')
  const location = useLocation()
  const navigate = useNavigate()
  const [resetPassword, {loading}] = useMutation(RESET_PASSWORD)
  const [forceChangePassword, {loading: forceLoading}] = useMutation(FORCE_CHANGE_PASSWORD)
  const [resetDone, setResetDone] = useState(false)
  const [showCurrentPassword, setShowCurrentPassword] = useState(false)
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)

  const params = new URLSearchParams(location.search)
  const forced = params.get('forced') === '1' || params.get('forced') === 'true'
  const email = params.get('email') || ''
  const uid = params.get('uid') || ''
  const token = params.get('token') || ''
  const isInvitation = params.get('invite') === '1' || params.get('invite') === 'true'
  const hasToken = Boolean(uid && token)
  const canSubmit = forced ? Boolean(email) : hasToken

  const formik = useFormik({
    initialValues: {
      currentPassword: '',
      password: '',
      confirmPassword: '',
    },
    validationSchema: Yup.object().shape({
      currentPassword: forced
        ? Yup.string()
            .min(
              PASSWORD_MIN_SYMBOLS,
              intl.formatMessage(M.minSymbols, {min: PASSWORD_MIN_SYMBOLS})
            )
            .max(
              PASSWORD_MAX_SYMBOLS,
              intl.formatMessage(M.maxSymbols, {max: PASSWORD_MAX_SYMBOLS})
            )
            .required(intl.formatMessage(M.currentPasswordRequired))
        : Yup.string().notRequired(),
      password: Yup.string()
        .min(
          PASSWORD_MIN_LENGTH,
          intl.formatMessage(PASSWORD_VALIDATION_MESSAGES.minLength, {min: PASSWORD_MIN_LENGTH})
        )
        .matches(/[A-Z]/, intl.formatMessage(PASSWORD_VALIDATION_MESSAGES.uppercase))
        .matches(/[a-z]/, intl.formatMessage(PASSWORD_VALIDATION_MESSAGES.lowercase))
        .matches(/[0-9]/, intl.formatMessage(PASSWORD_VALIDATION_MESSAGES.number))
        .matches(/[^A-Za-z0-9]/, intl.formatMessage(PASSWORD_VALIDATION_MESSAGES.special))
        .test(
          'not-same-as-current',
          intl.formatMessage(M.passwordDifferent),
          function (value) {
            if (!forced) return true
            const currentPassword = this.parent.currentPassword
            if (!value || !currentPassword) return true
            return value !== currentPassword
          }
        )
        .max(
          PASSWORD_MAX_SYMBOLS,
          intl.formatMessage(M.maxSymbols, {max: PASSWORD_MAX_SYMBOLS})
        )
        .required(intl.formatMessage(PASSWORD_VALIDATION_MESSAGES.required)),
      confirmPassword: Yup.string()
        .oneOf([Yup.ref('password')], intl.formatMessage(PASSWORD_VALIDATION_MESSAGES.match))
        .required(intl.formatMessage(M.confirmRequired)),
    }),
    onSubmit: async (values) => {
      if (!canSubmit) {
        toast.error(intl.formatMessage(forced ? M.missingEmail : M.invalidLink))
        return
      }

      try {
        const response = forced
          ? await forceChangePassword({
              variables: {
                email,
                currentPassword: values.currentPassword,
                newPassword: values.password,
              },
            })
          : await resetPassword({
              variables: {
                uid,
                token,
                newPassword: values.password,
              },
            })

        const payload = forced ? response.data?.forceChangePassword : response.data?.resetPassword
        const ok = payload?.ok
        const message = payload?.message || ''
        if (ok) {
          toast.success(message || intl.formatMessage(M.updated))
          setResetDone(true)
          setTimeout(() => navigate('/login'), 1200)
        } else {
          toast.error(
            message || intl.formatMessage(forced ? M.changeFailed : M.invalidLink)
          )
        }
      } catch (error) {
        toast.error(intl.formatMessage(M.genericError))
      }
    },
  })

  return (
    <div className='d-flex flex-column flex-lg-row flex-column-fluid h-100'>
      <div className='d-flex flex-column flex-lg-row-fluid w-lg-50 p-10 order-2 order-lg-1'>
        <div className='d-flex flex-center flex-column flex-lg-row-fluid'>
          <div className='w-lg-500px p-10'>
            <form className='form w-100' onSubmit={formik.handleSubmit} noValidate>
              <div className='text-center mb-11'>
                <img
                  src='/media/tfi/logoNColorP.png'
                  width={200}
                  alt={intl.formatMessage(M.logoAlt)}
                />
                <h1 className='text-dark fw-bolder mb-3'>
                  {intl.formatMessage(
                    forced ? M.titleForced : isInvitation ? M.titleInvite : M.titleReset
                  )}
                </h1>
                <div className='text-gray-500 fw-semibold fs-6'>
                  {intl.formatMessage(
                    forced
                      ? M.subtitleForced
                      : isInvitation
                        ? M.subtitleInvite
                        : M.subtitleReset
                  )}
                </div>
              </div>

              {!canSubmit && (
                <div className='mb-10 alert alert-danger'>
                  {intl.formatMessage(forced ? M.missingEmail : M.invalidLink)}
                </div>
              )}
              {resetDone && (
                <div className='mb-10 bg-light-info p-8 rounded'>
                  <div className='text-info'>{intl.formatMessage(M.doneRedirect)}</div>
                </div>
              )}

              <div
                className='mb-8 alert py-4'
                style={{
                  backgroundColor: '#EEF4FF',
                  color: '#0F172A',
                  border: '1px solid #93C5FD',
                }}
              >
                <div className='fw-bold mb-2'>{intl.formatMessage(M.requirementsTitle)}</div>
                <ul className='mb-0 ps-6'>
                  {PASSWORD_REQUIREMENTS.map((rule) => (
                    <li key={rule.id}>{intl.formatMessage(rule, {min: PASSWORD_MIN_LENGTH})}</li>
                  ))}
                  <li>{intl.formatMessage(M.requirementDifferent)}</li>
                </ul>
              </div>

              {forced && (
                <>
                  <div className='fv-row mb-8'>
                    <label className='form-label fs-6 fw-bolder text-dark'>
                      {intl.formatMessage(M.email)}
                    </label>
                    <input className='form-control bg-transparent' type='email' value={email} disabled />
                  </div>
                  <div className='fv-row mb-8'>
                    <label className='form-label fs-6 fw-bolder text-dark'>
                      {intl.formatMessage(M.fieldCurrentPassword)}
                    </label>
                    <div className='position-relative'>
                      <input
                        placeholder={intl.formatMessage(M.fieldCurrentPassword)}
                        {...formik.getFieldProps('currentPassword')}
                        className={clsx(
                          'form-control bg-transparent',
                          {'is-invalid': formik.touched.currentPassword && formik.errors.currentPassword},
                          {
                            'is-valid': formik.touched.currentPassword && !formik.errors.currentPassword,
                          }
                        )}
                        type={showCurrentPassword ? 'text' : 'password'}
                        name='currentPassword'
                      />
                      <button
                        type='button'
                        onClick={() => setShowCurrentPassword((p) => !p)}
                        style={{
                          color: '#111',
                          border: 0,
                          backgroundColor: 'transparent',
                          margin: 0,
                          padding: 0,
                          position: 'absolute',
                          right:
                            formik.touched.currentPassword && formik.errors.currentPassword
                              ? '36px'
                              : formik.touched.currentPassword
                                ? '36px'
                                : '10px',
                          bottom:
                            formik.touched.currentPassword && formik.errors.currentPassword
                              ? '35px'
                              : '10px',
                        }}
                      >
                        {showCurrentPassword ? (
                          <i className='bi bi-eye-slash fs-4' style={{color: 'inherit'}} />
                        ) : (
                          <i className='bi bi-eye fs-4' style={{color: 'inherit'}} />
                        )}
                      </button>
                    </div>
                    {formik.touched.currentPassword && formik.errors.currentPassword && (
                      <div className='fv-plugins-message-container'>
                        <span role='alert' className='text-danger'>
                          {formik.errors.currentPassword}
                        </span>
                      </div>
                    )}
                  </div>
                </>
              )}

              <div className='fv-row mb-8'>
                <label className='form-label fs-6 fw-bolder text-dark'>
                  {intl.formatMessage(M.fieldNewPassword)}
                </label>
                <div className='position-relative'>
                  <input
                    placeholder={intl.formatMessage(M.fieldNewPassword)}
                    {...formik.getFieldProps('password')}
                    className={clsx(
                      'form-control bg-transparent',
                      {'is-invalid': formik.touched.password && formik.errors.password},
                      {
                        'is-valid': formik.touched.password && !formik.errors.password,
                      }
                    )}
                    type={showNewPassword ? 'text' : 'password'}
                    name='password'
                  />
                  <button
                    type='button'
                    onClick={() => setShowNewPassword((p) => !p)}
                    style={{
                      color: '#111',
                      border: 0,
                      backgroundColor: 'transparent',
                      margin: 0,
                      padding: 0,
                      position: 'absolute',
                      right:
                        formik.touched.password && formik.errors.password
                          ? '36px'
                          : formik.touched.password
                            ? '36px'
                            : '10px',
                      bottom:
                        formik.touched.password && formik.errors.password ? '35px' : '10px',
                    }}
                  >
                    {showNewPassword ? (
                      <i className='bi bi-eye-slash fs-4' style={{color: 'inherit'}} />
                    ) : (
                      <i className='bi bi-eye fs-4' style={{color: 'inherit'}} />
                    )}
                  </button>
                </div>
                {formik.touched.password && formik.errors.password && (
                  <div className='fv-plugins-message-container'>
                    <span role='alert' className='text-danger'>
                      {formik.errors.password}
                    </span>
                  </div>
                )}
              </div>

              <div className='fv-row mb-8'>
                <label className='form-label fs-6 fw-bolder text-dark'>
                  {intl.formatMessage(M.fieldConfirmPassword)}
                </label>
                <div className='position-relative'>
                  <input
                    placeholder={intl.formatMessage(M.fieldConfirmPassword)}
                    {...formik.getFieldProps('confirmPassword')}
                    className={clsx(
                      'form-control bg-transparent',
                      {
                        'is-invalid':
                          formik.touched.confirmPassword && formik.errors.confirmPassword,
                      },
                      {
                        'is-valid':
                          formik.touched.confirmPassword && !formik.errors.confirmPassword,
                      }
                    )}
                    type={showConfirmPassword ? 'text' : 'password'}
                    name='confirmPassword'
                  />
                  <button
                    type='button'
                    onClick={() => setShowConfirmPassword((p) => !p)}
                    style={{
                      color: '#111',
                      border: 0,
                      backgroundColor: 'transparent',
                      margin: 0,
                      padding: 0,
                      position: 'absolute',
                      right:
                        formik.touched.confirmPassword && formik.errors.confirmPassword
                          ? '36px'
                          : formik.touched.confirmPassword
                            ? '36px'
                            : '10px',
                      bottom:
                        formik.touched.confirmPassword && formik.errors.confirmPassword
                          ? '35px'
                          : '10px',
                    }}
                  >
                    {showConfirmPassword ? (
                      <i className='bi bi-eye-slash fs-4' style={{color: 'inherit'}} />
                    ) : (
                      <i className='bi bi-eye fs-4' style={{color: 'inherit'}} />
                    )}
                  </button>
                </div>
                {formik.touched.confirmPassword && formik.errors.confirmPassword && (
                  <div className='fv-plugins-message-container'>
                    <span role='alert' className='text-danger'>
                      {formik.errors.confirmPassword}
                    </span>
                  </div>
                )}
              </div>

              <div className='d-grid mb-10'>
                <button
                  type='submit'
                  className='btn btn-primary mt-4'
                  disabled={!canSubmit || formik.isSubmitting || !formik.isValid || loading || forceLoading}
                >
                  {!loading && !forceLoading && (
                    <span className='indicator-label'>
                      {intl.formatMessage(
                        forced ? M.submitForced : isInvitation ? M.submitInvite : M.submitReset
                      )}
                    </span>
                  )}
                  {(loading || forceLoading) && (
                    <span className='indicator-progress' style={{display: 'block'}}>
                      {intl.formatMessage(M.pleaseWait)}
                      <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                    </span>
                  )}
                </button>
              </div>

              <div className='text-gray-500 text-center fw-semibold fs-6'>
                <Link to='/login' className='link-primary'>
                  {intl.formatMessage(M.backToLogin)}
                </Link>
              </div>
            </form>
          </div>
        </div>

        <div className='d-flex flex-center flex-wrap px-5 gap-3'>
          <LanguageToggle toggleBtnClass='btn-sm btn-active-light-primary border' />
          <div>
            <span>{VERSION_APP}</span>
          </div>
        </div>
      </div>

      <div
        className='d-flex flex-lg-row-fluid w-lg-50 bgi-size-cover bgi-position-center order-1 order-lg-2'
        style={{backgroundImage: `url(${toAbsoluteUrl('/media/misc/auth-bg.png')})`}}
      >
        <div className='d-flex flex-column flex-center py-15 px-5 px-md-15 w-100'>
          <span className='mb-12'>
            <img alt={intl.formatMessage(M.logoAlt)} src={defaultLogo} className='h-75px' />
          </span>
          <img
            className='mx-auto w-275px w-md-60 w-xl-500px mb-10 mb-lg-20'
            src={toAbsoluteUrl('/media/tfi/login.png')}
            alt=''
          />
          <h1 className='text-white fs-2qx fw-bolder text-center mb-7'>
            {intl.formatMessage(M.tagline)}
          </h1>
        </div>
      </div>
    </div>
  )
}
